import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';

export type Role = 'staff' | 'branch_head' | 'region_head' | 'general_head' | 'hr' | 'admin';
export type User = { sub?: string; id?: string; tenantId: string; name: string; phone: string; tenantName?: string; roles: Role[]; branchIds: string[]; regionIds: string[] };
type Auth = {
  user: User | null;
  restoring: boolean;
  requestOtp: (phone: string) => Promise<{ debugOtp?: string }>;
  verifyOtp: (phone: string, otp: string) => Promise<void>;
  logout: () => Promise<void>;
};

const Context = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    api.get<User>('/auth/me').then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setRestoring(false));
  }, []);

  const value = useMemo<Auth>(() => ({
    user,
    restoring,
    requestOtp: async (phone) => (await api.post('/auth/otp/request', { phone })).data,
    verifyOtp: async (phone, otp) => { const { data } = await api.post<{ user: User }>('/auth/otp/verify', { phone, otp }); setUser(data.user); },
    logout: async () => { try { await api.post('/auth/logout'); } finally { setUser(null); } },
  }), [user, restoring]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAuth() {
  const value = useContext(Context);
  if (!value) throw new Error('useAuth must be inside AuthProvider');
  return value;
}
